import React, { useState } from 'react';
import type { MediaAsset } from '../../../types/workspace';
import SecondaryActionButton from '../buttons/SecondaryActionButton';
import SmartImagePreview from '../../../components/shared/SmartImagePreview';

interface FileUploadFieldProps {
  label: string;
  description: string;
  onUpload: (file: File) => Promise<MediaAsset>;
}

const FileUploadField: React.FC<FileUploadFieldProps> = ({ label, description, onUpload }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUploadedAsset, setLastUploadedAsset] = useState<MediaAsset | null>(null);

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setIsUploading(true);
    setFeedback(null);
    setError(null);

    try {
      const asset = await onUpload(file);
      setFeedback(`Uploaded ${asset.altText || file.name}`);
      setLastUploadedAsset(asset);
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : 'Could not upload media');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  return (
    <div className="rounded-[28px] border border-white/10 bg-white/5 p-5">
      <div className="text-[11px] uppercase tracking-[0.35em] text-amber-100/45">{label}</div>
      <p className="mt-3 max-w-2xl text-sm leading-7 text-white/55">{description}</p>
      <p className="mt-2 text-sm text-white/40">
        Images keep their full frame automatically so cutout meals and transparent product shots stay clean.
      </p>
      <div className="mt-4">
        <label className="inline-flex cursor-pointer">
          <input type="file" accept="image/*" className="hidden" onChange={handleChange} />
          <SecondaryActionButton disabled={isUploading}>
            {isUploading ? 'Uploading...' : 'Upload Image'}
          </SecondaryActionButton>
        </label>
      </div>
      {lastUploadedAsset && (
        <div className="mt-4">
          <SmartImagePreview
            src={lastUploadedAsset.url}
            alt={lastUploadedAsset.altText || label}
            className="h-44"
          />
        </div>
      )}
      {feedback && <div className="mt-3 text-sm text-emerald-100">{feedback}</div>}
      {error && <div className="mt-3 text-sm text-red-200">{error}</div>}
    </div>
  );
};

export default FileUploadField;
